import { Fragment, useMemo, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';
import { navigate } from '../../app/navigation';
import { areaSectionKindOf, groupAreasBySection } from '../../domain/housePlan';
import { progressFor, type V2Area } from '../../domain/inspection';
import { useT } from '../../i18n/i18n';
import { areaSectionLabel, areaDisplayName } from '../../i18n/labels';
import { useStore } from '../../state/createStore';
import { findingStore } from '../../state/findingStore';
import { areasOf, inspectionStore, reorderAreaAction } from '../../state/inspectionStore';
import { areaIssueCount } from '../../state/issueCounts';
import { Icon, IconButton } from '../../ui/components';

// The grouped area list, shared by two screens with two modes (Authority §6.4):
//   layout   Property Layout (Figma 03 / 中文 08) — every area incl. disabled ones; per group a title
//            with ⊕ and a "添加区域" box; each row: drag handle · name (tap = all options: rename /
//            move / reorder / disable / delete) · pencil (rename) · trash (delete, KEEP data guard)
//   inspect  Inspection Overview (Figma 04 / 中文 02) — enabled areas only as cards: name ·
//            "n 项中已完成 m 项" · defects badge · chevron, or the done check; the area holding the
//            next uninspected item is outlined; tap enters the area (options live in Property Layout)
// Groups are derived from the Area records themselves (floor / catalog group) — never from a plan.
type DragState = { areaId: string; section: number; from: number; count: number; dy: number; pitch: number };

export function AreaGroups(props: {
  inspectionId: string;
  mode: 'layout' | 'inspect';
  onOptions: (area: V2Area) => void;
  onRename?: (area: V2Area) => void;
  onDelete?: (area: V2Area) => void;
  onAddArea?: (floor: number | null) => void;
  currentAreaId?: string | null;
}) {
  const { t, tp, locale } = useT();
  const state = useStore(inspectionStore, (s) => s);
  const findingState = useStore(findingStore, (s) => s);
  const areas = useMemo(() => areasOf(state, props.inspectionId), [state, props.inspectionId]);
  const listed = props.mode === 'layout' ? areas : areas.filter((a) => a.enabled);
  const sections = useMemo(() => groupAreasBySection(listed), [listed]);
  // Drag to reorder (Figma 08 handles): the dragged row follows the finger inside its own group,
  // the rows it passes slide out of the way, and on release the area steps into place through the
  // same swap the options sheet uses (reorderAreaAction). Arrow keys on the handle do the same.
  const [drag, setDrag] = useState<DragState | null>(null);
  const dragRef = useRef<(DragState & { startY: number }) | null>(null);

  function dragTarget(d: DragState): number {
    return Math.max(0, Math.min(d.count - 1, d.from + Math.round(d.dy / d.pitch)));
  }

  function startDrag(event: PointerEvent<HTMLButtonElement>, area: V2Area, section: number, from: number, count: number) {
    const row = event.currentTarget.closest('.lay-row');
    const pitch = (row?.getBoundingClientRect().height ?? 48) + 8;
    const next = { areaId: area.id, section, from, count, dy: 0, pitch };
    dragRef.current = { ...next, startY: event.clientY };
    event.currentTarget.setPointerCapture?.(event.pointerId);
    setDrag(next);
  }

  function moveDrag(event: PointerEvent<HTMLButtonElement>) {
    const current = dragRef.current;
    if (!current) return;
    const min = -current.from * current.pitch;
    const max = (current.count - 1 - current.from) * current.pitch;
    const dy = Math.max(min, Math.min(max, event.clientY - current.startY));
    dragRef.current = { ...current, dy };
    setDrag({ areaId: current.areaId, section: current.section, from: current.from, count: current.count, dy, pitch: current.pitch });
  }

  async function endDrag(commitMove: boolean) {
    const current = dragRef.current;
    dragRef.current = null;
    setDrag(null);
    if (!current || !commitMove) return;
    const steps = dragTarget(current) - current.from;
    for (let i = 0; i < Math.abs(steps); i += 1) await reorderAreaAction(current.areaId, steps < 0 ? 'up' : 'down');
  }

  function onHandleKey(event: KeyboardEvent<HTMLButtonElement>, area: V2Area) {
    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
    event.preventDefault();
    void reorderAreaAction(area.id, event.key === 'ArrowUp' ? 'up' : 'down');
  }

  function rowShift(section: number, index: number, areaId: string): number {
    if (!drag || drag.section !== section || drag.areaId === areaId) return 0;
    const target = dragTarget(drag);
    if (target === drag.from) return 0;
    const lower = Math.min(drag.from, target);
    const upper = Math.max(drag.from, target);
    if (index < lower) return 0;
    if (index > upper) return 0;
    return target === upper ? -drag.pitch : drag.pitch;
  }

  if (sections.length === 0) {
    return (
      <p className="card-note area-list-empty" data-testid="area-list-empty">
        {t('layout.allDisabled')}
      </p>
    );
  }

  return (
    <div className="list list-grouped" data-testid="area-list">
      {sections.map(({ section, areas: rows }) => {
        const kind = areaSectionKindOf(section);
        const enabledRows = rows.filter((a) => a.enabled);
        if (props.mode === 'inspect') {
          return (
            <Fragment key={section.order}>
              <h3 className="ov-group-title" data-testid="area-section" data-section-order={section.order}>
                {areaSectionLabel(t, section)}
              </h3>
              {enabledRows.map((area) => {
                const progress = progressFor(state.items.filter((i) => i.areaId === area.id));
                // B2 (2026-10-27): the badge counts what the overview's 问题记录 counts — 缺陷 rows plus saved other findings.
                const issues = areaIssueCount(state, findingState, area.id);
                const done = area.completedAt !== null;
                const current = !done && area.id === props.currentAreaId;
                return (
                  <button
                    key={area.id}
                    type="button"
                    className={`area-card ${done ? 'area-card-done' : ''} ${current ? 'area-card-current' : ''}`}
                    onClick={() => navigate({ screen: 'areaWorkspace', inspectionId: props.inspectionId, areaId: area.id, itemSheetId: null })}
                    data-name={area.name}
                    data-testid="area-row"
                  >
                    <span className="area-card-main">
                      <span className="area-card-title">{areaDisplayName(locale, area)}</span>
                      {done ? (
                        <span className="area-card-sub">{tp('overview.areaDone', progress.total)}</span>
                      ) : (
                        <span className="area-card-sub" data-testid="area-progress-pill">
                          {tp('overview.areaProgress', progress.total, { done: progress.settled })}
                        </span>
                      )}
                      {issues > 0 ? (
                        <span className="badge area-card-badge" data-testid="area-issue-badge" data-count={issues}>
                          {tp('common.findingsCount', issues)}
                        </span>
                      ) : null}
                    </span>
                    <Icon name={done ? 'check_circle' : 'chevron_right'} className={done ? 'area-card-check' : 'area-card-chevron'} />
                  </button>
                );
              })}
            </Fragment>
          );
        }
        const floorOfSection = kind.kind === 'floor' ? kind.floor : null;
        return (
          <Fragment key={section.order}>
            <div className="lay-group-head" data-testid="area-section" data-section-order={section.order}>
              <h2 className="group-title lay-group-title">{areaSectionLabel(t, section)}</h2>
              {props.onAddArea ? (
                <IconButton
                  icon="add_circle"
                  label={t('layout.addArea')}
                  onClick={() => props.onAddArea?.(floorOfSection)}
                  className="lay-group-add"
                  testId={`add-area-section-${section.order}`}
                />
              ) : null}
            </div>
            {rows.map((area, index) => {
              const dragged = drag?.areaId === area.id;
              const shift = dragged ? drag.dy : rowShift(section.order, index, area.id);
              const name = areaDisplayName(locale, area);
              return (
                <div
                  key={area.id}
                  className={`lay-row ${area.enabled ? '' : 'lay-row-disabled'} ${dragged ? 'lay-row-dragging' : ''}`}
                  style={shift !== 0 ? { transform: `translateY(${shift}px)` } : undefined}
                  data-name={area.name}
                  data-testid="area-row"
                >
                  <button
                    type="button"
                    className="lay-drag"
                    aria-label={t('layout.dragHandle', { name })}
                    onPointerDown={(e) => startDrag(e, area, section.order, index, rows.length)}
                    onPointerMove={moveDrag}
                    onPointerUp={() => void endDrag(true)}
                    onPointerCancel={() => void endDrag(false)}
                    onKeyDown={(e) => onHandleKey(e, area)}
                    data-testid="area-drag"
                  >
                    <Icon name="drag_indicator" size={20} />
                  </button>
                  <button type="button" className="lay-row-name" onClick={() => props.onOptions(area)} aria-label={`${name} · ${t('layout.areaOptions')}`} data-testid="area-menu">
                    <span className="lay-row-title">{name}</span>
                    {area.enabled ? null : <span className="lay-row-tag">{t('layout.disabledTag')}</span>}
                  </button>
                  {props.onRename ? <IconButton icon="edit" label={t('layout.rename')} onClick={() => props.onRename?.(area)} className="lay-row-edit" testId="area-rename" /> : null}
                  {props.onDelete ? <IconButton icon="delete" label={t('common.delete')} onClick={() => props.onDelete?.(area)} className="lay-row-delete" testId="area-delete" /> : null}
                </div>
              );
            })}
            {props.onAddArea ? (
              <button type="button" className="lay-add-box" onClick={() => props.onAddArea?.(floorOfSection)} data-testid={`add-area-box-${section.order}`}>
                {t('layout.addArea')}
              </button>
            ) : null}
          </Fragment>
        );
      })}
    </div>
  );
}
